import React, { useState, useEffect, useMemo, useRef } from "react";
import "./style.css";
import { ShimmerThumbnail } from "react-shimmer-effects";
import { MY_APPOINMENT_CLEAR, SET_APPOINTMENT_DATA } from "../../constant";
import { connect } from "react-redux";
import {
  HiOutlineCalendar,
  HiOutlineClipboardList,
  HiOutlineMap,
  HiOutlineSearch,
  HiOutlineShieldCheck,
} from "react-icons/hi";
import Carcard from "../../components/Car_Card/index";
import { useHistory } from "react-router-dom";
import { scrollTop } from "../../utils/window";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { getAuthToken, getAuthUserId } from "../../utils/userIdentity";
import {
  fetchUserEvaluations,
  filterEvaluationsByUserId,
} from "../../utils/accountActivity";
import { sortAppointmentVehiclesForList } from "../../utils/appointmentBooking";

const HERO_IMAGE = "/images/hero-appointment-detail.jpg";

const LOOKUP_STEPS = [
  "Vehicles for your account load automatically when you open this page.",
  "Optionally enter a postcode to filter the list.",
  "Open a vehicle to view photos, booking, and inspection progress.",
];

function normalizePostcode(value) {
  return String(value || "")
    .replace(/\s+/g, "")
    .toUpperCase();
}

function getEvaluationPostcode(item) {
  if (!item || typeof item !== "object") return "";
  const addr = Array.isArray(item.vehicle_address)
    ? item.vehicle_address[0]
    : item.vehicle_address;
  return (
    item.Postcode ||
    item.postcode ||
    item.post_code ||
    addr?.post_code ||
    addr?.postcode ||
    addr?.Postcode ||
    ""
  );
}

function filterEvaluationsByPostcode(items, postcode) {
  const needle = normalizePostcode(postcode);
  if (!needle) return Array.isArray(items) ? items : [];
  return (items || []).filter((item) => {
    const code = normalizePostcode(getEvaluationPostcode(item));
    if (!code) return false;
    return code === needle || code.includes(needle) || needle.includes(code);
  });
}

function Myappointment(props) {
  const { feedback } = useContext(FeedbackContext);
  const myAppointment_page = feedback.myAppointment_page || {};

  const { auth, myappointment_Clear, setAppointmentData } = props;
  const history = useHistory();

  const userId = getAuthUserId(auth);
  const token = getAuthToken(auth);
  const isLoggedIn = Boolean(token);

  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [postcode, setPostcode] = useState("");
  const [activePostcodeFilter, setActivePostcodeFilter] = useState("");
  const autoLoadedRef = useRef(false);

  const displayedVehicles = useMemo(() => {
    if (activePostcodeFilter) {
      return filterEvaluationsByPostcode(allVehicles, activePostcodeFilter);
    }
    return allVehicles;
  }, [allVehicles, activePostcodeFilter]);

  const loadVehicles = () => {
    if (!isLoggedIn || !userId || !token) {
      setAllVehicles([]);
      setAppointmentData([]);
      setLoading(false);
      setHasLoaded(true);
      setLoadError("");
      return;
    }

    setLoading(true);
    setLoadError("");

    fetchUserEvaluations({ token, userId })
      .then((result) => {
        const raw = sortAppointmentVehiclesForList(
          filterEvaluationsByUserId(result.raw || [], userId)
        );
        setAllVehicles(raw);
        setAppointmentData(raw);
        setHasLoaded(true);
      })
      .catch(() => {
        setAllVehicles([]);
        setAppointmentData([]);
        setHasLoaded(true);
        setLoadError(
          "Could not load your vehicles. Please try again in a moment."
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (history.action === "PUSH") {
      myappointment_Clear();
    }
    scrollTop();
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userId || !token) {
      setAllVehicles([]);
      setAppointmentData([]);
      setLoading(false);
      setHasLoaded(false);
      setLoadError("");
      setActivePostcodeFilter("");
      autoLoadedRef.current = false;
      return undefined;
    }

    if (autoLoadedRef.current) return undefined;
    autoLoadedRef.current = true;
    loadVehicles();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userId, token]);

  useEffect(() => {
    setAppointmentData(displayedVehicles);
  }, [displayedVehicles, setAppointmentData]);

  const hasResults =
    Array.isArray(displayedVehicles) && displayedVehicles.length > 0;

  const handleFilter = (event) => {
    event.preventDefault();
    setActivePostcodeFilter(normalizePostcode(postcode));
  };

  const clearFilter = () => {
    setPostcode("");
    setActivePostcodeFilter("");
  };

  const resultsContent = !isLoggedIn ? (
    <div className="pi-appointment__empty pi-appointment__empty--panel">
      <HiOutlineCalendar size={36} aria-hidden />
      <h3>Sign in to view appointments</h3>
      <p>Your vehicles load automatically after you sign in.</p>
      <button
        type="button"
        className="pi-appointment__submit"
        style={{ marginTop: "1rem", maxWidth: "14rem" }}
        onClick={() => history.push("/login")}
      >
        Log in
      </button>
    </div>
  ) : loading ? (
    <div
      className="pi-appointment__results-list"
      aria-busy="true"
      aria-label="Loading appointments"
    >
      {[0, 1].map((key) => (
        <div className="pi-appointment__skeleton-row" key={key}>
          <ShimmerThumbnail height={140} rounded />
          <div className="pi-appointment__skeleton-copy">
            <ShimmerThumbnail height={22} width="42%" rounded />
            <ShimmerThumbnail height={16} width="68%" rounded />
            <ShimmerThumbnail height={14} width="88%" rounded />
          </div>
        </div>
      ))}
    </div>
  ) : hasResults ? (
    <div className="pi-appointment__results-list">
      {displayedVehicles.map((item, index) => (
        <Carcard
          key={item.id || item.reg_number || `appointment-${index}`}
          item={item}
          index={index}
          mycar={displayedVehicles}
          onClick={() => {
            history.push({
              pathname: "/myappointmentdetail",
              state: { index, evaluationId: item.id },
            });
            scrollTop();
          }}
        />
      ))}
    </div>
  ) : hasLoaded ? (
    <div className="pi-appointment__empty pi-appointment__empty--panel">
      <HiOutlineCalendar size={36} aria-hidden />
      <h3>
        {loadError
          ? "Could not load vehicles"
          : activePostcodeFilter
            ? "No vehicles for this postcode"
            : myAppointment_page.no_results_title || "No vehicles found"}
      </h3>
      <p>
        {loadError ||
          (activePostcodeFilter
            ? `No vehicles matched postcode ${activePostcodeFilter}.`
            : "No evaluations are linked to your account yet.")}
      </p>
      {activePostcodeFilter ? (
        <button
          type="button"
          className="pi-appointment__submit"
          style={{ marginTop: "1rem", maxWidth: "14rem" }}
          onClick={clearFilter}
        >
          Clear postcode filter
        </button>
      ) : null}
    </div>
  ) : (
    <div className="pi-appointment__welcome">
      <div className="pi-appointment__welcome-icon" aria-hidden>
        <HiOutlineClipboardList size={28} />
      </div>
      <h3>
        {myAppointment_page.welcome_title || "Your vehicles will appear here"}
      </h3>
      <p>Loading vehicles for your account…</p>
    </div>
  );

  return (
    <div className="pi-appointment">
      <header className="pi-inner-hero pi-inner-hero--with-bar">
        <div className="pi-inner-hero__media" aria-hidden="true">
          <img
            className="pi-inner-hero__media-img"
            src={`${process.env.PUBLIC_URL || ""}${HERO_IMAGE}`}
            alt=""
          />
          <span className="pi-inner-hero__media-shade" />
        </div>
        <div className="pi-inner-hero__fx" aria-hidden="true">
          <span className="pi-inner-hero__glow pi-inner-hero__glow--a" />
          <span className="pi-inner-hero__glow pi-inner-hero__glow--b" />
          <span className="pi-inner-hero__grid" />
        </div>
        <div className="pi-inner-hero__content">
          <div className="pi-inner-hero__copy">
            <p className="pi-label pi-inner-hero__label">
              {myAppointment_page.hero_label || "Track your sale"}
            </p>
            <h1 className="pi-inner-hero__title">
              {myAppointment_page.My_Appointment_headin || "My appointments"}
            </h1>
            <p className="pi-inner-hero__lead">
              {isLoggedIn
                ? "All of your vehicles load automatically. Use postcode to filter the list."
                : "Sign in to see your vehicles and appointments."}
            </p>
          </div>
        </div>
        <span className="pi-inner-hero__fade" aria-hidden="true" />
      </header>

      <section className="pi-appointment__body">
        <div className="pi-appointment__inner">
          <div className="pi-appointment__stage">
            <aside className="pi-appointment__aside">
              <div className="pi-appointment__lookup-card">
                <header className="pi-appointment__lookup-head">
                  <span className="pi-appointment__eyebrow pi-appointment__eyebrow--accent">
                    <HiOutlineSearch size={15} aria-hidden />
                    {myAppointment_page.lookup_eyebrow || "Filter list"}
                  </span>
                  <h2>Filter by postcode</h2>
                  <p>
                    Your vehicles are already loaded. Enter a postcode only if
                    you want to narrow the list.
                  </p>
                </header>

                <form onSubmit={handleFilter} className="pi-appointment__form">
                  <label className="pi-appointment__field">
                    <span className="pi-appointment__field-label">
                      {myAppointment_page.postcode_label || "Postcode"}
                    </span>
                    <span className="pi-appointment__field-control">
                      <HiOutlineMap size={18} aria-hidden />
                      <input
                        className="pi-appointment__input"
                        placeholder={
                          myAppointment_page.post_code_placeholde ||
                          "e.g. SW1A 1AA"
                        }
                        value={postcode}
                        onChange={(e) =>
                          setPostcode(String(e.target.value || "").toUpperCase())
                        }
                        disabled={!isLoggedIn || loading}
                        autoComplete="postal-code"
                      />
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="pi-appointment__submit"
                    name="CTAButton"
                    disabled={!isLoggedIn || loading}
                  >
                    {activePostcodeFilter
                      ? "Apply postcode filter"
                      : "Filter by postcode"}
                  </button>

                  {activePostcodeFilter ? (
                    <button
                      type="button"
                      className="pi-appointment__submit pi-appointment__submit--ghost"
                      onClick={clearFilter}
                      disabled={loading}
                    >
                      Clear filter ({activePostcodeFilter})
                    </button>
                  ) : null}
                </form>
              </div>

              <div className="pi-appointment__info-card pi-evaluation__info-card">
                <h3>
                  <HiOutlineShieldCheck size={18} aria-hidden />
                  {myAppointment_page.how_it_works_title || "How it works"}
                </h3>
                <ol className="pi-evaluation__info-list">
                  {LOOKUP_STEPS.map((text, stepIndex) => (
                    <li key={text}>
                      <span className="pi-evaluation__info-num" aria-hidden>
                        {stepIndex + 1}
                      </span>
                      <span className="pi-evaluation__info-text">{text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <div className="pi-appointment__panel">
              <header className="pi-appointment__panel-head">
                <span className="pi-appointment__eyebrow">
                  <HiOutlineCalendar size={16} aria-hidden />
                  {myAppointment_page.results_eyebrow || "Your garage"}
                </span>
                <div className="pi-appointment__panel-title-row">
                  <h2>{myAppointment_page.results_title || "Your vehicles"}</h2>
                  {!loading && hasResults ? (
                    <span className="pi-appointment__results-count">
                      {displayedVehicles.length}
                    </span>
                  ) : null}
                </div>
                <p>
                  {!isLoggedIn
                    ? "Sign in to see vehicles."
                    : activePostcodeFilter
                      ? `Showing vehicles for postcode ${activePostcodeFilter}.`
                      : myAppointment_page.results_lead ||
                        "Open a vehicle to review booking details, photos, and your inspection profile."}
                </p>
              </header>

              <div className="pi-appointment__results-shell">{resultsContent}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  myappointment_Clear: () =>
    dispatch({
      type: MY_APPOINMENT_CLEAR,
    }),
  setAppointmentData: (data) =>
    dispatch({
      type: SET_APPOINTMENT_DATA,
      payload: { data },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Myappointment);
